import React, { useState } from "react";
import { Settings, CheckCircle, RefreshCw, X } from "lucide-react";
import { JsonEditorModal } from "../JsonEditorModal/JsonEditorModal";
import useConfig from "../../demos/hooks/useConfig";
import { CadviewerContext } from "../../context/Cadviewer.Context";
import { ViewsConfigModal } from "./ViewsConfig/ViewsConfigModal";

interface ApplicationSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ApplicationSetupModal: React.FC<ApplicationSetupModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { config } = useConfig();
  const { user, fetchViewJson } = React.useContext(CadviewerContext);
  
  const [planType, setPlanType] = useState(() => localStorage.getItem("appSetup_planType") || "Lease Plan");
  
  const [isJsonEditorOpen, setIsJsonEditorOpen] = useState(false);
  const [jsonConfigData, setJsonConfigData] = useState(() => localStorage.getItem("appSetup_jsonConfigData") || "");
  const [jsonEditorTitle, setJsonEditorTitle] = useState("");
  const [activeJsonFile, setActiveJsonFile] = useState<string | null>(() => localStorage.getItem("appSetup_activeJsonFile") || null);

  const [plansStatus, setPlansStatus] = useState(() => localStorage.getItem("appSetup_plansStatus") || "Not Generated");
  const [searchStatus, setSearchStatus] = useState(() => localStorage.getItem("appSetup_searchStatus") || "Not Generated");
  const [viewsStatus, setViewsStatus] = useState(() => localStorage.getItem("appSetup_viewsStatus") || "Not Generated");

  const [generatedSearchPayload, setGeneratedSearchPayload] = useState<string>(() => localStorage.getItem("appSetup_generatedSearchPayload") || "");
  const [isViewsConfigModalOpen, setIsViewsConfigModalOpen] = useState(false);
  const [loadedViewsConfig, setLoadedViewsConfig] = useState<any[] | undefined>(undefined);

  React.useEffect(() => {
    localStorage.setItem("appSetup_planType", planType);
    localStorage.setItem("appSetup_jsonConfigData", jsonConfigData);
    if (activeJsonFile) {
      localStorage.setItem("appSetup_activeJsonFile", activeJsonFile);
    } else {
      localStorage.removeItem("appSetup_activeJsonFile");
    }
    localStorage.setItem("appSetup_plansStatus", plansStatus);
    localStorage.setItem("appSetup_searchStatus", searchStatus);
    localStorage.setItem("appSetup_viewsStatus", viewsStatus);
    localStorage.setItem("appSetup_generatedSearchPayload", generatedSearchPayload);
  }, [planType, jsonConfigData, activeJsonFile, plansStatus, searchStatus, viewsStatus, generatedSearchPayload]);

  /**
   * Which of the three files are already on the server.
   *
   * The statuses above are remembered in localStorage, which makes them a
   * record of what THIS browser did rather than of what exists: a frontend
   * whose application folder arrived with a marketplace pack, or one opened
   * from another machine, showed "Not Generated" for files that were sitting
   * right there — and the buttons that read them stayed greyed out. Asking the
   * server is the only answer that is true for everyone.
   */
  const [onServer, setOnServer] = useState<Record<string, boolean>>({});

  React.useEffect(() => {
    if (!isOpen) return;
    const location = config.globalApplicationFilesLocation;
    if (!location) return;
    const base = location.endsWith("/") ? location : `${location}/`;
    let cancelled = false;

    (async () => {
      const names = ["plans.json", "search.json", "views.json"];
      const found: Record<string, boolean> = {};
      await Promise.all(
        names.map(async (name) => {
          try {
            // A GET, not a HEAD: this is exactly the request the Load buttons
            // make, so a file this says is there is a file they can read.
            const res = await fetch(base + name, { cache: "no-store" });
            found[name] = res.ok;
          } catch {
            found[name] = false;
          }
        })
      );
      if (cancelled) return;
      setOnServer(found);
      // Only correct a status that claims nothing exists; a file being edited
      // right now has more to say about itself than the server does.
      const mark = (set: React.Dispatch<React.SetStateAction<string>>, name: string) =>
        set((current) => (found[name] && current === "Not Generated" ? "On server" : current));
      mark(setPlansStatus, "plans.json");
      mark(setSearchStatus, "search.json");
      mark(setViewsStatus, "views.json");
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, config.globalApplicationFilesLocation]);

  if (!isOpen) return null;

  const clipUrl = (fullUrl: string) => {
    let path = "";
    try {
      const urlObj = new URL(fullUrl);
      path = urlObj.pathname;
    } catch {
      path = fullUrl;
    }
    const cleanSub = config.serverSubFolder ? config.serverSubFolder.replace(/\/$/, "") : "";
    if (cleanSub && path.includes(cleanSub)) {
      path = path.substring(path.indexOf(cleanSub) + cleanSub.length);
    }
    if (!path.startsWith("/")) path = "/" + path;
    if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
    return path;
  };

  const getRelativeSelectedPath = (fullUrl: string) => {
    let path = "";
    try {
      const urlObj = new URL(fullUrl);
      path = urlObj.pathname;
    } catch {
      path = fullUrl;
    }
    const contentIndex = path.indexOf("/content/");
    if (contentIndex !== -1) {
      path = path.substring(contentIndex + "/content/".length);
    }
    if (path.length > 1 && path.endsWith("/")) path = path.slice(0, -1);
    return path;
  };

  const findFolderByPath = (folderList: any[], targetPath: string): any | null => {
    const normalizedTarget = targetPath.replace(/\\/g, '/').toLowerCase();
    
    for (const folder of folderList) {
      if (folder.path) {
        const normalizedFolder = folder.path.replace(/\\/g, '/').toLowerCase();
        if (normalizedFolder.endsWith(normalizedTarget) || normalizedFolder.endsWith(normalizedTarget + '/')) {
          return folder;
        }
      }
      if (folder.children) {
        const found = findFolderByPath(folder.children, targetPath);
        if (found) return found;
      }
    }
    return null;
  };

  const handleGeneratePlans = async () => {
    try {
      setPlansStatus("Generating...");
      let endpoint = "listdwgdirectory";
      const backendUrl = config.ServerBackEndUrl || "http://localhost:3000";
      if (!backendUrl.endsWith("/")) endpoint = "/" + endpoint;
      const headers = new Headers();
      if (user?.token && config.enableLoginFeature) {
        headers.append("Authorization", `Bearer ${user.token}`);
      }
      headers.append("Content-Type", "application/json");

      // root=content, or the search below never finds the svg/json folders:
      // without it the tree starts at the drawings, one level past them.
      const response = await fetch(
        backendUrl + endpoint + `?loginFeatureEnabled=${config.enableLoginFeature}&root=content`,
        { method: "GET", headers }
      );
      
      if (!response.ok) throw new Error("Failed to load folder structure from server");
      const folderStructure = await response.json();

      const svgRelativePath = clipUrl(config.propertySVGFilesLocation);
      const targetFolder = findFolderByPath(folderStructure, svgRelativePath);

      if (!targetFolder || !targetFolder.children) {
        alert(`Could not find SVG directory on server matching: ${svgRelativePath}`);
        setPlansStatus("Error");
        return;
      }

      const jsonRelativePath = clipUrl(config.propertyJSONFilesLocation);
      const jsonFolder = findFolderByPath(folderStructure, jsonRelativePath);
      let jsonBaseUrl = config.propertyJSONFilesLocation;
      
      if (jsonFolder && jsonFolder.path) {
        const normJsonPath = jsonFolder.path.replace(/\\/g, '/');
        const contentIdx = normJsonPath.indexOf("/content/");
        if (contentIdx !== -1) {
          const relativeToContent = normJsonPath.substring(contentIdx);
          const backendUrl = config.ServerBackEndUrl || "http://localhost:3000";
          jsonBaseUrl = backendUrl + (backendUrl.endsWith("/") ? relativeToContent.substring(1) : relativeToContent);
        }
      }

      const propertiesMap = new Map<string, any>();
      const searchPlansArray: any[] = [];
      
      const fetchPromises = targetFolder.children
        .filter((f: any) => !f.isFolder && f.name.toLowerCase().endsWith(".svg"))
        .map(async (file: any) => {
          const nameWithoutExt = file.name.substring(0, file.name.lastIndexOf('.'));
          const lastDashIdx = nameWithoutExt.lastIndexOf('-');
          if (lastDashIdx === -1) return null;

          const propertyNumber = nameWithoutExt.substring(0, lastDashIdx);
          const planName = nameWithoutExt.substring(lastDashIdx + 1);

          let dataObj: any = {};
          try {
            const jsonUrl = jsonBaseUrl + 
              (jsonBaseUrl.endsWith("/") ? "" : "/") + 
              nameWithoutExt + ".json";
            const res = await fetch(jsonUrl);
            if (res.ok) {
              const text = await res.text();
              if (!text.startsWith("Error")) {
                const jsonFile = JSON.parse(text);
                dataObj = jsonFile.data || {};
              }
            }
          } catch (e) {
            console.error("Failed to fetch property JSON for", nameWithoutExt, e);
          }

          return {
            propertyNumber,
            planName,
            nameWithoutExt,
            svgFileName: file.name,
            dataObj
          };
        });

      const results = await Promise.all(fetchPromises);

      for (const res of results) {
        if (!res) continue;
        const { propertyNumber, planName, nameWithoutExt, svgFileName, dataObj } = res;
        const pName = dataObj.propertyName || `Property ${propertyNumber}`;

        if (!propertiesMap.has(propertyNumber)) {
          propertiesMap.set(propertyNumber, {
            propertyName: pName,
            propertyNumber: propertyNumber,
            plans: []
          });
        }
        
        propertiesMap.get(propertyNumber).plans.push({
          dataFileName: `${nameWithoutExt}.json`,
          planDisplayName: `${pName} - ${planName}`,
          planName: planName,
          svgFileName: svgFileName,
          Address: dataObj.Address || "",
          City: dataObj.City || "",
          State: dataObj.State || "",
          PostalCode: dataObj.PostalCode || ""
        });

        const addressParts = [dataObj.Address, dataObj.City, dataObj.State, dataObj.PostalCode].filter(Boolean);
        searchPlansArray.push({
          dataFileName: `${nameWithoutExt}.json`,
          planDisplayName: `${pName} - ${planName}`,
          planName: planName,
          svgFileName: svgFileName,
          propertyName: pName,
          propertyNumber: propertyNumber,
          "Building Address": addressParts.join(", "),
          spaces: Array.isArray(dataObj.spaces) ? dataObj.spaces.map((s: any) => ({
            spaceHeader: s.spaceHeader || "",
            spaceNumber: String(s.spaceNumber || "")
          })) : []
        });
      }

      const generatedPlans = {
        data: [
          {
            displayOrder: 1,
            planType: planType,
            properties: Array.from(propertiesMap.values())
          }
        ]
      };

      const generatedSearch = {
        data: [
          {
            displayOrder: 1,
            planType: planType,
            plans: searchPlansArray
          }
        ]
      };

      setGeneratedSearchPayload(JSON.stringify(generatedSearch, null, 2));

      setJsonEditorTitle("plans.json");
      setJsonConfigData(JSON.stringify(generatedPlans, null, 2));
      setActiveJsonFile("plans.json");
      setIsJsonEditorOpen(true);
      setPlansStatus("Generated (Unsaved)");
    } catch (e: any) {
      console.error(e);
      alert("Error generating plans.json: " + e.message);
      setPlansStatus("Error");
    }
  };

  const uploadFileToServer = async (filename: string, content: string) => {
    let endpoint = "settings/folders/upload";
    const backendUrl = config.ServerBackEndUrl || "http://localhost:3000";
    if (!backendUrl.endsWith("/")) endpoint = "/" + endpoint;

    const appFilesPath = getRelativeSelectedPath(config.globalApplicationFilesLocation);
    
    const formData = new FormData();
    const blob = new Blob([content], { type: "application/json" });
    formData.append("files", blob, filename);
    formData.append("global", "true");
    formData.append("path", appFilesPath);

    const headers = new Headers();
    if (user?.token && config.enableLoginFeature) {
      headers.append("Authorization", `Bearer ${user.token}`);
    }

    const response = await fetch(backendUrl + endpoint, {
      method: "POST",
      body: formData,
      headers
    });

    if (!response.ok) {
      throw new Error(`Failed to save ${filename}`);
    }
  };

  const handleUpdatePlans = async () => {
    if (!jsonConfigData || activeJsonFile !== "plans.json") {
      alert("Please generate plans.json first.");
      return;
    }

    try {
      setPlansStatus("Saving...");
      await uploadFileToServer("plans.json", jsonConfigData);
      setPlansStatus("Saved");
      
      if (generatedSearchPayload) {
        setSearchStatus("Saving...");
        await uploadFileToServer("search.json", generatedSearchPayload);
        setSearchStatus("Saved");
      }
      
      window.dispatchEvent(new CustomEvent("applicationFilesUpdated"));
      alert("Successfully updated plans.json and search.json");
    } catch(e: any) {
      console.error(e);
      alert("Error saving plans.json: " + e.message);
      setPlansStatus("Error");
    }
  };

  const handleLoadSearch = async () => {
    try {
      setSearchStatus("Loading...");
      let jsonBaseUrl = config.globalApplicationFilesLocation;
      if (!jsonBaseUrl.endsWith("/")) jsonBaseUrl += "/";
      const res = await fetch(jsonBaseUrl + "search.json");
      if (!res.ok) throw new Error("search.json not found on server.");
      const data = await res.json();
      
      setJsonEditorTitle("search.json");
      setJsonConfigData(JSON.stringify(data, null, 2));
      setActiveJsonFile("search.json");
      setIsJsonEditorOpen(true);
      setSearchStatus("Loaded");
    } catch(e: any) {
      alert("Error loading search.json: " + e.message);
      setSearchStatus("Error");
    }
  };

  const handleUpdateSearch = async () => {
    if (!jsonConfigData || activeJsonFile !== "search.json") {
      alert("Please load search.json first.");
      return;
    }
    try {
      setSearchStatus("Saving...");
      await uploadFileToServer("search.json", jsonConfigData);
      setSearchStatus("Saved");
      alert("Successfully updated search.json");
    } catch(e: any) {
      console.error(e);
      alert("Error saving search.json: " + e.message);
      setSearchStatus("Error");
    }
  };


  const handleCreateViews = () => {
    setLoadedViewsConfig(undefined);
    setIsViewsConfigModalOpen(true);
  };

  const handleLoadViews = async () => {
    try {
      setViewsStatus("Loading...");
      let jsonBaseUrl = config.globalApplicationFilesLocation;
      if (!jsonBaseUrl.endsWith("/")) jsonBaseUrl += "/";
      const res = await fetch(jsonBaseUrl + "views.json");
      if (!res.ok) throw new Error("views.json not found on server.");
      const data = await res.json();
      
      if (data && data.data && data.data.length > 0) {
        const firstGroup = data.data[0];
        const parsedViews = (firstGroup.views || []).map((v: any, index: number) => {
          const offLayers = (v.layers || [])
            .filter((l: any) => String(l.layerState || l.LayerState || l.state || "").toLowerCase() === "off")
            .map((l: any) => l.layer || l.Layer || l.name || l.layerName || "");
          
          return {
            id: Date.now().toString() + index,
            name: v.view || v.View || "Unknown View",
            offLayers: offLayers
          };
        });
        setLoadedViewsConfig(parsedViews);
      } else {
        setLoadedViewsConfig(undefined);
      }
      
      setViewsStatus("Loaded");
      setIsViewsConfigModalOpen(true);
    } catch(e: any) {
      alert("Error loading views.json: " + e.message);
      setViewsStatus("Error");
    }
  };

  const handleGenerateViewsData = (viewsConfigList: any[]) => {
    const generatedViews = {
      data: [
        {
          displayOrder: 5,
          planType: planType,
          selected: viewsConfigList.length > 0 ? viewsConfigList[0].name : "",
          views: viewsConfigList.map((v, index) => ({
            view: v.name,
            View: v.name,
            selected: index === 0,
            displayOrder: index + 1,
            initialLayerState: "AllOn",
            layers: v.offLayers.map((layerId: string) => ({
              colorOverride: "none",
              layer: layerId,
              layerState: "Off"
            }))
          }))
        }
      ]
    };
    setJsonEditorTitle("views.json");
    setJsonConfigData(JSON.stringify(generatedViews, null, 2));
    setActiveJsonFile("views.json");
    setIsJsonEditorOpen(true);
    setViewsStatus("Created (Unsaved)");
    setIsViewsConfigModalOpen(false);
  };

  const handleUpdateViews = async () => {
    if (!jsonConfigData || activeJsonFile !== "views.json") {
      alert("Please create views.json first.");
      return;
    }
    try {
      setViewsStatus("Saving...");
      await uploadFileToServer("views.json", jsonConfigData);
      setViewsStatus("Saved");
      await fetchViewJson();
      alert("Successfully updated views.json and refreshed application state.");
    } catch(e: any) {
      console.error(e);
      alert("Error saving views.json: " + e.message);
      setViewsStatus("Error");
    }
  };

  const rows = [
    { title: "plans.json", status: plansStatus, onBtn1: handleGeneratePlans, btn1Text: "Generate", onBtn2: handleUpdatePlans, btn2Text: "Update", btn2Disabled: activeJsonFile !== "plans.json" && plansStatus !== "Generated (Unsaved)" },
    // Reading a file that is already there has nothing to do with whether
    // plans.json was saved in this session — which is what used to gate both of
    // these, and left every button grey on a frontend set up from a pack.
    { title: "search.json", status: searchStatus, onBtn1: handleLoadSearch, btn1Text: "Load", onBtn2: handleUpdateSearch, btn2Text: "Update", btn1Disabled: !onServer["search.json"] && plansStatus !== "Saved", btn2Disabled: activeJsonFile !== "search.json" },
    { title: "views.json", status: viewsStatus, onBtn1: handleCreateViews, btn1Text: "Create", onBtn2: handleUpdateViews, btn2Text: "Update", btn2Disabled: activeJsonFile !== "views.json", onBtn3: handleLoadViews, btn3Text: "Load Current", btn3Disabled: !onServer["views.json"] && plansStatus !== "Saved" },
  ];

  return (
    <>
      <div className="flex flex-col flex-1 overflow-hidden bg-white">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">VizQuery Application Setup</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 p-1 rounded-md"
          >
            <span className="sr-only">Close panel</span>
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="px-4 py-5 flex-1 overflow-y-auto">
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-indigo-100 sm:mx-0 sm:h-10 sm:w-10">
              <Settings className="h-6 w-6 text-indigo-600" aria-hidden="true" />
            </div>
            <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
              <h3 className="text-base font-semibold leading-6 text-gray-900">
                Application Files Setup
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Configure and generate the required application JSON files.
              </p>

              <div className="mt-4 text-left">
                <label htmlFor="planType" className="block text-sm font-medium text-gray-700">
                  Plan Type
                </label>
                <input
                  type="text"
                  name="planType"
                  id="planType"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border px-3 py-2"
                  value={planType}
                  onChange={(e) => setPlanType(e.target.value)}
                  placeholder="e.g. Lease Plan"
                />
              </div>

              <div className="mt-6 border border-gray-200 rounded-md shadow-sm overflow-hidden">
                <ul className="divide-y divide-gray-200">
                  {rows.map((row, index) => (
                    <li key={index} className="px-4 py-4 sm:px-6 hover:bg-gray-50 flex flex-col sm:flex-row sm:items-center justify-between transition-colors gap-3">
                      <div className="flex flex-col text-left">
                        <span className="text-sm font-medium text-gray-900">
                          {row.title}
                        </span>
                        <span className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                          Status: {row.status}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <button
                          className={`inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-1.5 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 ${row.btn1Disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                          onClick={row.onBtn1}
                          disabled={row.btn1Disabled}
                        >
                          <CheckCircle className="-ml-0.5 h-4 w-4 text-green-500" aria-hidden="true" />
                          {row.btn1Text}
                        </button>
                          <button
                            className={`inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-1.5 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 ${row.btn2Disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                            onClick={row.onBtn2}
                            disabled={row.btn2Disabled}
                          >
                            <RefreshCw className="-ml-0.5 h-4 w-4 text-blue-500" aria-hidden="true" />
                            {row.btn2Text}
                          </button>
                          {row.onBtn3 && (
                            <button
                              className={`inline-flex items-center gap-x-1.5 rounded-md bg-white px-3 py-1.5 text-xs font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 ${row.btn3Disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                              onClick={row.onBtn3}
                              disabled={row.btn3Disabled}
                            >
                              <CheckCircle className="-ml-0.5 h-4 w-4 text-amber-500" aria-hidden="true" />
                              {row.btn3Text}
                            </button>
                          )}
                        </div>
                      </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <JsonEditorModal
        isOpen={isJsonEditorOpen}
        onClose={() => setIsJsonEditorOpen(false)}
        title={jsonEditorTitle}
        configData={jsonConfigData}
        setConfigData={setJsonConfigData}
        readOnly={false}
        allowVersioning={false}
        onSave={(saveAsNewVersion, versionName) => {
          setIsJsonEditorOpen(false);
          // Just updates memory, user must hit 'Update' to save to server
        }}
      />
      <ViewsConfigModal
        isOpen={isViewsConfigModalOpen}
        onClose={() => setIsViewsConfigModalOpen(false)}
        onGenerate={handleGenerateViewsData}
        planType={planType}
        initialViews={loadedViewsConfig}
      />
    </>
  );
};
